import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Users, ChevronRight, Lock } from 'lucide-react';
import { cardShadow } from '../lib/designTokens';

export const RosterTab = ({
  players,
  team,
  user,
  isHeadCoach,
  isAssistantCoach,
  effectiveIsElite,
  devReviewProposals,
  showRetired,
  setShowRetired,
  playerSeasonStats,
  playerLastGame,
  playerHotCold,
  setIsAddingGuestPlayer,
  setIsAddingPlayer,
  setShowDevReview,
  setEditingPlayer,
  setScoutingPlayer,
  setEliteModal,
}: {
  players: any[];
  team: any;
  user: any;
  isHeadCoach: boolean;
  isAssistantCoach: boolean;
  effectiveIsElite: boolean;
  devReviewProposals: any[];
  showRetired: boolean;
  setShowRetired: any;
  playerSeasonStats: Map<string, any>;
  playerLastGame: Map<string, any>;
  playerHotCold: Map<string, any>;
  setIsAddingGuestPlayer: any;
  setIsAddingPlayer: any;
  setShowDevReview: any;
  setEditingPlayer: any;
  setScoutingPlayer: any;
  setEliteModal: any;
}) => {
  return (
    <>
              {/* Section header */}
              <div style={{ padding: '14px 16px 6px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <div>
                  <h2 className="text-2xl font-black italic uppercase tracking-tight">Roster</h2>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                    {players.length} player{players.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {isHeadCoach && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <button
                      onClick={() => setIsAddingGuestPlayer(true)}
                      style={{ fontSize: '12px', fontWeight: 600, color: '#5A6672', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      + Guest
                    </button>
                    <button
                      onClick={() => setIsAddingPlayer(true)}
                      style={{ fontSize: '12px', fontWeight: 600, color: '#141A21', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                      + Add Player
                    </button>
                  </div>
                )}
              </div>

              {/* Development Review prompt — only when there are pending proposals */}
              {isHeadCoach && devReviewProposals.length > 0 && (
                <div
                  onClick={() => setShowDevReview(true)}
                  style={{
                    margin: '10px 16px 4px',
                    padding: '12px 14px',
                    backgroundColor: '#141A21',
                    borderRadius: '16px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#FFFFFF' }}>
                      Development Review
                    </div>
                    <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', marginTop: '2px' }}>
                      {devReviewProposals.length} player{devReviewProposals.length !== 1 ? 's' : ''} where xOVR has diverged from scouted ratings
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: '#FFFFFF', color: '#141A21',
                    borderRadius: '12px', padding: '3px 10px',
                    fontSize: '12px', fontWeight: 700, flexShrink: 0,
                  }}>
                    {devReviewProposals.length}
                  </div>
                </div>
              )}

              {players.length === 0 ? (
                <div style={{ margin: '8px 16px 16px', padding: '48px 16px', textAlign: 'center', backgroundColor: '#FFFFFF', borderRadius: '16px', boxShadow: cardShadow }}>
                  <Users style={{ width: '40px', height: '40px', color: '#E3E7EB', margin: '0 auto 12px' }} />
                  <p style={{ fontSize: '13px', fontWeight: 400, color: '#5A6672' }}>No players drafted yet.</p>
                  {isHeadCoach && (
                    <button
                      onClick={() => setIsAddingPlayer(true)}
                      style={{ marginTop: '12px', padding: '8px 16px', borderRadius: '8px', backgroundColor: '#141A21', color: '#FFFFFF', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
                    >
                      Add First Player
                    </button>
                  )}
                </div>
              ) : (
                // One card per player on the canvas, in place of a divided list.
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '8px 16px 16px' }}>
                  {players.filter(p => !p.retired).sort((a,b) => (b.ovr || 0) - (a.ovr || 0)).map(player => {
                    const inactiveGuest = player.isGuest && player.guestActive === false;
                    const season = playerSeasonStats.get(player.id);
                    const last = playerLastGame.get(player.id);
                    const hc = playerHotCold.get(player.id);
                    return (
                      <div
                        key={player.id}
                        onClick={() => {
                          if (isHeadCoach) setEditingPlayer(player);
                          else if (isAssistantCoach) setScoutingPlayer(player);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 14px', borderRadius: '16px', boxShadow: cardShadow,
                          backgroundColor: '#FFFFFF',
                          cursor: (isHeadCoach || isAssistantCoach) ? 'pointer' : 'default',
                          opacity: inactiveGuest ? 0.45 : 1,
                          gap: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                          {/* Jersey circle + xOVR badge */}
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div style={{
                              width: '40px', height: '40px', borderRadius: '50%',
                              backgroundColor: player.isGuest ? '#F1F3F5' : '#141A21',
                              color: player.isGuest ? '#5A6672' : '#FFFFFF',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '14px', fontWeight: 600,
                            }}>
                              {player.number}
                            </div>
                            {/* Hot/cold rides the jersey corner, as on Stats and Home's
                                Who's Hot, so it never wraps under a long name. */}
                            {hc?.batting && (
                              <span style={{ position: 'absolute', bottom: '-5px', right: '-6px', fontSize: '13px', lineHeight: 1 }}>
                                {hc.batting === 'hot' ? '🔥' : '❄️'}
                              </span>
                            )}
                            {isHeadCoach && effectiveIsElite && (
                              <div style={{
                                position: 'absolute', top: '-3px', right: '-7px',
                                minWidth: '20px', height: '20px', borderRadius: '10px', padding: '0 4px',
                                backgroundColor: '#FFFFFF', border: '0.5px solid #E3E7EB',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <span style={{ fontSize: '10px', fontWeight: 600, color: '#141A21' }}>{player.ovr || 0}</span>
                              </div>
                            )}
                            {isHeadCoach && !effectiveIsElite && (
                              <div
                                onClick={(e) => { e.stopPropagation(); setEliteModal('upgrade'); }}
                                style={{
                                  position: 'absolute', top: '-3px', right: '-7px',
                                  width: '20px', height: '20px', borderRadius: '50%',
                                  backgroundColor: '#FFFFFF', border: '0.5px solid #E3E7EB',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                                }}
                              >
                                <Lock style={{ width: '10px', height: '10px', color: '#8B919A' }} />
                              </div>
                            )}
                          </div>

                          {/* Name + meta */}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '15px', fontWeight: 600, color: '#141A21' }}>
                                {player.name || 'Unknown Player'}
                              </span>
                              {player.isGuest && (
                                <span style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', backgroundColor: '#F1F3F5', padding: '1px 6px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Guest
                                </span>
                              )}
                              {isAssistantCoach && (
                                <span style={{ fontSize: '10px', fontWeight: 600, color: player.assistantRatings?.[user?.uid ?? ''] ? '#141A21' : '#8B919A' }}>
                                  {player.assistantRatings?.[user?.uid ?? ''] ? 'Scouted ✓' : 'Scout →'}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '11px', fontWeight: 400, color: '#5A6672', marginTop: '1px' }}>
                              {[player.primaryPosition || 'UTL', player.secondaryPosition].filter(Boolean).join(' · ')}
                              {last && last.ab > 0 && !player.isGuest ? ` · Last: ${last.h}-for-${last.ab}` : ''}
                            </div>
                          </div>
                        </div>

                        {/* Stats + affordance */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                          {season && season.avg > 0 && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#141A21', lineHeight: 1 }}>{season.avg.toFixed(3).replace(/^0/, '')}</div>
                              <div style={{ fontSize: '9px', fontWeight: 400, color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '2px' }}>AVG</div>
                            </div>
                          )}
                          {season && season.ops > 0 && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#141A21', lineHeight: 1 }}>{season.ops.toFixed(3).replace(/^0/, '')}</div>
                              <div style={{ fontSize: '9px', fontWeight: 400, color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '2px' }}>OPS</div>
                            </div>
                          )}
                          {season && season.ip > 0 && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: '#141A21', lineHeight: 1 }}>{season.era.toFixed(2)}</div>
                              <div style={{ fontSize: '9px', fontWeight: 400, color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '2px' }}>ERA</div>
                            </div>
                          )}
                          {player.isGuest ? (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const newActive = player.guestActive === false ? true : false;
                                try {
                                  await setDoc(doc(db, `teams/${team.id}/players`, player.id), { guestActive: newActive }, { merge: true });
                                } catch (err) { console.error('Failed to toggle guest active', err); }
                              }}
                              style={{
                                padding: '4px 10px', borderRadius: '6px',
                                fontSize: '11px', fontWeight: 600,
                                color: player.guestActive === false ? '#8B919A' : '#141A21',
                                border: `1px solid ${player.guestActive === false ? '#E3E7EB' : '#141A21'}`,
                                backgroundColor: 'transparent', cursor: 'pointer',
                              }}
                            >
                              {player.guestActive === false ? 'Inactive' : 'Active'}
                            </button>
                          ) : (
                            <ChevronRight style={{ width: '16px', height: '16px', color: '#8B919A' }} />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Retired Players — collapsed section */}
                  {players.filter(p => p.retired).length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <button
                        onClick={() => setShowRetired(v => !v)}
                        style={{
                          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 2px 2px', backgroundColor: 'transparent', border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          Retired Players ({players.filter(p => p.retired).length})
                        </span>
                        <ChevronRight style={{ width: '16px', height: '16px', color: '#8B919A', transform: showRetired ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
                      </button>
                      {showRetired && players.filter(p => p.retired).sort((a,b) => (b.ovr || 0) - (a.ovr || 0)).map(player => (
                        <div
                          key={player.id}
                          onClick={() => { if (isHeadCoach) setEditingPlayer(player); }}
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '12px 14px', borderRadius: '16px', boxShadow: cardShadow,
                            backgroundColor: '#FFFFFF', cursor: isHeadCoach ? 'pointer' : 'default',
                            opacity: 0.55, gap: '12px',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                            <div style={{
                              width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                              backgroundColor: '#F1F3F5', color: '#5A6672',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '14px', fontWeight: 600,
                            }}>
                              {player.number}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '15px', fontWeight: 600, color: '#141A21' }}>{player.name || 'Unknown Player'}</span>
                                <span style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', backgroundColor: '#F1F3F5', padding: '1px 6px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Retired</span>
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: 400, color: '#5A6672', marginTop: '1px' }}>
                                {[player.primaryPosition || 'UTL', player.secondaryPosition].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                          </div>
                          {isHeadCoach && (
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await setDoc(doc(db, `teams/${team.id}/players`, player.id), { retired: false }, { merge: true });
                                } catch (err) { console.error('Failed to reactivate player', err); }
                              }}
                              style={{
                                padding: '4px 10px', borderRadius: '6px', flexShrink: 0,
                                fontSize: '11px', fontWeight: 600, color: '#10B981',
                                border: '1px solid #10B981', backgroundColor: 'transparent', cursor: 'pointer',
                              }}
                            >
                              Reactivate
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
    </>
  );
};
